import { tint, transparentize } from 'polished';
import styled, { css } from 'styled-components';

import { media } from '@savgroup-front-common/constants';
import { SUPPORTED_PROPERTIES } from '@savgroup-front-common/types';

import {
  getCustomComponentProperty,
  rem,
  styledGetCustomComponentProperty,
} from '../../../helpers';
import { ACCORDION_ROLE } from '../Accordion.types';

interface $AccordionHeaderProps {
  $isErrored: boolean;
  $isActive: boolean;
  $isDisabled: boolean;
  $isList?: boolean;
  $role: ACCORDION_ROLE;
  $componentThemeName?: string;
  $isNewUiEnabled: boolean;
  $isColored: boolean;
  $hasAdditionalTitleContent: boolean;
}

export const $AccordionHeader = styled.div<$AccordionHeaderProps>`
  width: 100%;

  display: flex;
  gap: 0.5rem;
  align-items: ${({ $hasAdditionalTitleContent }) =>
    $hasAdditionalTitleContent ? 'start' : 'center'};
  justify-content: space-between;
  background-color: ${({
    $role,
    theme,
    $isErrored,
    $isActive,
    $isDisabled,
    $componentThemeName,
    $isList,
    $isNewUiEnabled,
    $isColored,
  }) => {
    if ($isColored && $isActive) {
      return transparentize(0.9, theme.colors.primary);
    }

    if (
      ($isList && !$isNewUiEnabled) ||
      ($isList && $isNewUiEnabled && !$isActive)
    ) {
      return theme.systemColors.defaultBackground;
    }

    if ($componentThemeName) {
      const getBackgroundColor = styledGetCustomComponentProperty({
        componentName: $componentThemeName,
        property: SUPPORTED_PROPERTIES.BACKGROUND_COLOR,
      });
      const backgroundColor = getBackgroundColor({ theme });

      if (backgroundColor) {
        return backgroundColor;
      }
    }
    if ($isDisabled) {
      return $isActive
        ? theme.colors.default
        : theme.systemColors.secondaryBackground;
    }
    if ($isErrored) {
      return $isActive ? theme.colors.danger : tint(0.85, theme.colors.danger);
    }

    switch ($role) {
      case ACCORDION_ROLE.PRIMARY:
        return theme.colors.headerTitleColor;
      case ACCORDION_ROLE.SECONDARY:
        return theme.colors.secondaryColor2;
      default:
        throw new Error('not supported role.');
    }
  }};

  border: ${({ $isList, $isNewUiEnabled, theme }) => {
    if ($isNewUiEnabled) {
      return `1px solid ${theme.newUI.dividerColors.default}`;
    }

    if ($isList) {
      return 'none';
    }

    return '1px solid #9a30470d';
  }};

  border-radius: ${({ $isActive, $componentThemeName, theme, $isList }) => {
    if ($isList) {
      return `0`;
    }

    return $isActive
      ? `${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || rem(5)
        } ${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || rem(5)
        } 0 0`
      : `${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || rem(5)
        } ${
          getCustomComponentProperty({
            theme,
            componentName: $componentThemeName,
            property: SUPPORTED_PROPERTIES.BORDER_RADIUS,
          }) || rem(5)
        }`;
  }};

  height: ${({ $componentThemeName, $hasAdditionalTitleContent }) => {
    if ($hasAdditionalTitleContent) {
      return `${rem(70)}`;
    }

    return styledGetCustomComponentProperty({
      property: SUPPORTED_PROPERTIES.HEIGHT,
      componentName: $componentThemeName,
      fallback: `${rem(50)}`,
    });
  }};
  padding: ${({ $hasAdditionalTitleContent }) =>
    $hasAdditionalTitleContent ? '1rem 1rem' : '0 1rem'};
  cursor: ${({ $isDisabled }) => ($isDisabled ? undefined : 'pointer')};

  @media ${media.maxWidth.sm} {
    padding: 1rem;
  }
`;

interface $AccordionTitleProps {
  $color: string;
  $isList?: boolean;
}
export const $AccordionTitle = styled.div<$AccordionTitleProps>`
  color: ${({ $color }) => $color};
  display: flex;
  flex-direction: row;
  ${({ $isList }) => ($isList ? 'width: 100%' : '')}
`;

export const $AccordionHeaderAdditionalTitleContent = styled.div`
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  font-size: ${({ theme }) => theme.fonts.size.megaSmall};
  font-weight: ${({ theme }) => theme.fonts.weight.normal};
`;

const side = css`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
`;

export const $LeftSideContainer = styled.div<{
  $isList?: boolean;
}>`
  ${side};
  font-weight: ${({ theme }) => theme.fonts.weight.bold};
  user-select: none;

  display: flex;
  flex-direction: column;
  align-items: start;
  margin-right: auto;
  ${({ $isList }) => ($isList ? 'width: 100%' : '')}
`;

export const $AccordionHeaderAdditionalContent = styled.div`
  color: ${({ theme }) => theme.newUI.defaultColors.secondaryText};
  font-size: ${({ theme }) => theme.fonts.size.megaSmall};
  font-weight: ${({ theme }) => theme.fonts.weight.normal};
`;

export const $RightSideContainer = styled.div`
  ${side}
  >svg {
    margin-left: 1rem;
  }
`;
